import { Camera, Loader, X } from "lucide-react";
import React, { useRef } from "react";
import { motion } from "framer-motion";
import Modal from "../../../components/common/Modal";
import CategorySelect from "../CategorySelect";
import { useTranslation } from "react-i18next";

const IdeaFormModal = ({
  onClose,
  newIdea,
  setNewIdea,
  handleSubmit,
  loadingSubmit,
  handleImageChange,
  handleRemoveImage,
  open,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const headerContent = (
    <div>
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="tw-text-2xl tw-font-bold tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-transparent"
      >
        {t("ideas.form.title")}
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="tw-text-gray-500 tw-mt-2"
      >
        {t("ideas.form.subtitle")}
      </motion.p>
    </div>
  );

  const footerContent = (
    <motion.button
      whileHover={
        !loadingSubmit
          ? {
              scale: 1.05,
              boxShadow: "0 15px 30px rgba(0,0,0,0.15)",
            }
          : {}
      }
      whileTap={!loadingSubmit ? { scale: 0.95 } : {}}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 15,
      }}
      type="submit"
      form="idea-form"
      disabled={loadingSubmit}
      className="tw-w-full tw-px-8 tw-py-3.5 tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-500 tw-text-white tw-rounded-xl tw-text-base tw-font-medium tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-space-x-2"
    >
      {loadingSubmit ? (
        <>
          <Loader className="tw-w-5 tw-h-5 tw-animate-spin" />
          <span>{t("ideas.form.sharing")}</span>
        </>
      ) : (
        <span>{t("ideas.form.share_button")}</span>
      )}
    </motion.button>
  );

  return (
    <Modal
      onClose={onClose}
      headerContent={headerContent}
      footerContent={footerContent}
      open={open}
    >
      <form
        id="idea-form"
        onSubmit={handleSubmit}
        className="tw-space-y-8 tw-pb-4"
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <label className="tw-block tw-text-base tw-font-medium tw-bg-gradient-to-r tw-from-gray-800 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
            {t("ideas.form.destination_label")}
          </label>
          <motion.input
            whileFocus={{ scale: 1.01 }}
            type="text"
            required
            placeholder={t("ideas.form.destination_placeholder")}
            className="tw-mt-2 tw-block tw-w-full tw-rounded-xl tw-border-0 tw-bg-gray-50 tw-px-4 tw-py-3.5 tw-text-gray-900 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-gray-200 placeholder:tw-text-gray-400 focus:tw-ring-2 focus:tw-ring-[deepskyblue] tw-transition-all"
            value={newIdea.destination}
            onChange={(e) =>
              setNewIdea({
                ...newIdea,
                destination: e.target.value,
              })
            }
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <label className="tw-block tw-text-base tw-font-medium tw-bg-gradient-to-r tw-from-gray-800 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
            {t("ideas.form.category_label")}
          </label>
          <CategorySelect
            value={newIdea.category}
            onChange={(value) => setNewIdea({ ...newIdea, category: value })}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <label className="tw-block tw-text-base tw-font-medium tw-bg-gradient-to-r tw-from-gray-800 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
            {t("ideas.form.description_label")}
          </label>
          <motion.textarea
            whileFocus={{ scale: 1.01 }}
            rows={6}
            placeholder={t("ideas.form.description_placeholder")}
            className="tw-mt-2 tw-block tw-w-full tw-rounded-xl tw-border-0 tw-bg-gray-50 tw-px-4 tw-py-3.5 tw-text-gray-900 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-gray-200 placeholder:tw-text-gray-400 focus:tw-ring-2 focus:tw-ring-[deepskyblue] tw-transition-all"
            value={newIdea.description}
            onChange={(e) =>
              setNewIdea({
                ...newIdea,
                description: e.target.value,
              })
            }
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
          className="tw-space-y-4"
        >
          <label className="tw-block tw-text-base tw-font-medium tw-bg-gradient-to-r tw-from-gray-800 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
            {t("ideas.form.photos_label")}
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleImageChange}
            className="tw-hidden"
          />
          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            {newIdea.imagePreviews.map((preview, index) => (
              <div key={index} className="tw-relative">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="tw-w-full tw-h-48 tw-object-cover tw-rounded-xl"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="tw-absolute tw-top-2 tw-right-2 tw-p-1 tw-bg-white tw-rounded-full hover:tw-bg-gray-100"
                >
                  <X className="tw-w-4 tw-h-4 tw-text-gray-500" />
                </button>
              </div>
            ))}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="tw-cursor-pointer tw-relative tw-w-full tw-h-48 tw-rounded-xl tw-bg-gray-50 tw-border-2 tw-border-dashed tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100 tw-transition-colors"
            >
              <div className="tw-text-center">
                <Camera className="tw-w-8 tw-h-8 tw-text-gray-400 tw-mx-auto tw-mb-2" />
                <p className="tw-text-sm tw-text-gray-500">
                  {t("ideas.form.upload_photos")}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </form>
    </Modal>
  );
};

export default IdeaFormModal;
